import { z } from 'zod/v4';

import { advancedBorderSettingsSchema } from '../../../common/schemas/legacy';

/**
 * The image widget's structured **styles** group — the data carried by the
 * envelope's `styles` field. Unlike every other widget (whose `styles` is the
 * open JSS `styleBagSchema` record), the image widget persists a typed bag of
 * border / radius / max-width settings here, mirroring the FormGroup produced
 * by `getImageWidgetForm`'s `styles` group in the host app.
 *
 * Every leaf is `nullish()`: the host form types mark the radius corners
 * optional (the styles renderer falls back to `borderRadius` when a corner is
 * absent — see `image-view.component.styles.ts`), and widgets persisted before
 * a given field shipped omit it. `advancedBorderSettings` is a single
 * `FormControl<AdvancedBorderSettings | null>` in the host (defaults to
 * `null`), so it's `nullable()` here.
 */
/**
 * Editing bounds for the border-radius corners, declared once here as the
 * schema-level source of truth. Attached to each radius leaf via `.meta()`
 * (self-documenting) and enforced on write by the signal-form accessors, which
 * clamp with {@link clampToBounds}. The image control reads these for its
 * `sp-input-num` `[min]`/`[max]` instead of the app-level `CSS_RESTRICTIONS`.
 */
export const IMAGE_BORDER_RADIUS_BOUNDS = { min: 0, max: 250 } as const;

export const imageStylesSchemaLegacy = z.object({
    borderRadius: z.number().nullish().meta(IMAGE_BORDER_RADIUS_BOUNDS),
    borderTopLeftRadius: z.number().nullish().meta(IMAGE_BORDER_RADIUS_BOUNDS),
    borderTopRightRadius: z.number().nullish().meta(IMAGE_BORDER_RADIUS_BOUNDS),
    borderBottomRightRadius: z.number().nullish().meta(IMAGE_BORDER_RADIUS_BOUNDS),
    borderBottomLeftRadius: z.number().nullish().meta(IMAGE_BORDER_RADIUS_BOUNDS),
    maxWidth: z.number().nullish(),
    borderWidth: z.number().nullish(),
    borderStyle: z.string().nullish(),
    borderColor: z.string().nullish(),
    advancedBorderSettings: advancedBorderSettingsSchema.nullable(),
});

export type ImageStylesLegacy = z.infer<typeof imageStylesSchemaLegacy>;

/**
 * Default image styles bag — mirrors the `styles` FormGroup defaults in
 * `getImageWidgetForm` (zeroed radii, 100 max-width, 1px solid black border,
 * no advanced border overrides).
 */
export const createImageStylesLegacy = (overrides: Partial<ImageStylesLegacy> = {}): ImageStylesLegacy => ({
    borderRadius: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    maxWidth: 100,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000',
    advancedBorderSettings: null,
    ...overrides,
});
